import React, { useState } from 'react';
import { addSampleProducts, addSampleOrders, addSampleSales } from '../utils/sampleData';
import '../styles/SetupData.css';

const SetupData = () => {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);

  const addMessage = (message, type = 'info') => {
    setMessages(prev => [...prev, { text: message, type, timestamp: new Date() }]);
  };

  const handleAddProducts = async () => {
    setLoading(true);
    addMessage('Starting to add sample products...', 'info');

    const result = await addSampleProducts();

    if (result.success) {
      addMessage(`✅ Successfully added ${result.count} products!`, 'success');
    } else {
      addMessage(`❌ Error: ${result.error}`, 'error');
    }

    setLoading(false);
  };

  const handleAddOrders = async () => {
    setLoading(true);
    addMessage('Starting to add sample orders...', 'info');

    const result = await addSampleOrders(3);

    if (result.success) {
      addMessage(`✅ Successfully added ${result.count} pending orders!`, 'success');
    } else {
      addMessage(`❌ Error: ${result.error}`, 'error');
    }

    setLoading(false);
  };

  const handleAddSales = async () => {
    setLoading(true);
    addMessage('Starting to add sample sales data...', 'info');

    const result = await addSampleSales();

    if (result.success) {
      addMessage(`✅ Successfully added ${result.count} sales records!`, 'success');
    } else {
      addMessage(`❌ Error: ${result.error}`, 'error');
    }

    setLoading(false);
  };

  const handleAddAll = async () => {
    setLoading(true);
    setMessages([]);

    addMessage('🚀 Starting complete setup...', 'info');

    // Add products
    addMessage('Adding products...', 'info');
    const productsResult = await addSampleProducts();
    if (productsResult.success) {
      addMessage(`✅ Added ${productsResult.count} products`, 'success');
    } else {
      addMessage(`❌ Products error: ${productsResult.error}`, 'error');
    }

    // Wait a bit
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Add orders
    addMessage('Adding orders...', 'info');
    const ordersResult = await addSampleOrders(3);
    if (ordersResult.success) {
      addMessage(`✅ Added ${ordersResult.count} orders`, 'success');
    } else {
      addMessage(`❌ Orders error: ${ordersResult.error}`, 'error');
    }

    // Wait a bit
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Add sales
    addMessage('Adding sales data...', 'info');
    const salesResult = await addSampleSales();
    if (salesResult.success) {
      addMessage(`✅ Added ${salesResult.count} sales records`, 'success');
    } else {
      addMessage(`❌ Sales error: ${salesResult.error}`, 'error');
    }

    addMessage('🎉 Setup complete! You can now explore the application.', 'success');
    setLoading(false);
  };

  const clearMessages = () => {
    setMessages([]);
  };

  return (
    <div className="setup-data">
      <div className="setup-container">
        <h1>Setup Sample Data</h1>
        <p className="setup-description">
          Use this page to populate your database with sample data for testing.
          This is useful when setting up the application for the first time.
        </p>

        <div className="setup-cards">
          <div className="setup-card">
            <h3>📦 Products</h3>
            <p>Add 12 sample products including burgers, pizza, salads, and drinks.</p>
            <button
              onClick={handleAddProducts}
              disabled={loading}
              className="setup-btn"
            >
              Add Sample Products
            </button>
          </div>

          <div className="setup-card">
            <h3>🛒 Orders</h3>
            <p>Add 3 pending orders that cashiers can review and confirm.</p>
            <button
              onClick={handleAddOrders}
              disabled={loading}
              className="setup-btn"
            >
              Add Sample Orders
            </button>
          </div>

          <div className="setup-card">
            <h3>💰 Sales Data</h3>
            <p>Add sample sales records for testing the reporting features.</p>
            <button
              onClick={handleAddSales}
              disabled={loading}
              className="setup-btn"
            >
              Add Sample Sales
            </button>
          </div>
        </div>

        <div className="setup-action">
          <button
            onClick={handleAddAll}
            disabled={loading}
            className="setup-all-btn"
          >
            {loading ? '⏳ Setting up...' : '🚀 Setup Everything'}
          </button>
        </div>

        {messages.length > 0 && (
          <div className="messages-section">
            <div className="messages-header">
              <h3>Setup Log</h3>
              <button onClick={clearMessages} className="clear-btn">
                Clear
              </button>
            </div>
            <div className="messages-list">
              {messages.map((msg, index) => (
                <div key={index} className={`message ${msg.type}`}>
                  <span className="message-text">{msg.text}</span>
                  <span className="message-time">
                    {msg.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="setup-info">
          <h3>ℹ️ Important Notes:</h3>
          <ul>
            <li>Make sure you're logged in before adding data</li>
            <li>Check Firestore rules allow authenticated writes</li>
            <li>You can run this multiple times (it will add duplicate data)</li>
            <li>Products will appear in "User Order" view</li>
            <li>Orders will appear in "Cashier View"</li>
            <li>Sales data will show in "Daily Report" and "Yearly Report"</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SetupData;
